/**
 * useRecordActions — shared action logic for a CRM record's overflow menu.
 *
 * Builds the flat "simple" actions (Research, Enrich phone number, Delete) plus
 * the lifecycle-stage "Add to CRM" submenu data, and owns the delete
 * confirmation modal. Consumed by:
 *   - RecordActionMenu (full-page kebab — renders stage sections + simple items)
 *   - RecordHeader's slideout action bar (merges simple actions into a single
 *     ResponsiveActionBar alongside entity CTAs)
 *
 * Centralizing here keeps the two surfaces in lock-step and avoids duplicating
 * the delete / enrich / research wiring.
 */

import {
  Archive,
  CheckDone01,
  Edit03,
  MessageChatCircle,
  RefreshCcw01,
  SearchLg,
  Stars02,
  Trash01,
} from '@untitledui/icons';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFeatureAccess, usePermissions } from '@/hooks/usePermissions';
import type { ResponsiveAction } from '@/lib/base/buttons';
import { buildEditInBuilderNavState } from '@/lib/crm/components/badges/buildBuilderModeNavState';
import { buildChatAboutNavState } from '@/lib/crm/components/badges/buildChatAboutNavState';
import { DeleteCRMObjectModal } from '@/lib/crm/components/DeleteCRMObjectModal';
import { usePhoneEnrichment } from '@/lib/crm/components/enrichment/usePhoneEnrichment';
import { useMergeRecordAction } from '@/lib/crm/components/merge/useMergeRecordAction';
import type { CrmObject, EntityTypeName } from '@/lib/crm/core/types';
import { useCanEditInBuilder } from '@/lib/crm/hooks/useCanEditInBuilder';
import { useCreateDeal } from '@/lib/crm/hooks/useCreateDeal';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { getNoteKindFromObject } from '@/lib/crm/record/memo/noteKind';
import {
  currentSingleObjectDeleteJobGeneration,
  startSingleObjectDeleteJob,
} from '@/lib/crm/store/singleObjectDeleteJobStore';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { logger } from '@/lib/logger';
import {
  getPropertyValue,
  isAsyncSingleObjectDeleteResponse,
} from '@/types/crm';

import { useConvertNoteToTask } from '../hooks/useConvertNoteToTask';
import { useCrmObjectMutations } from '../hooks/useCRMObject';
import { getEntityTypeConfig } from '../registry/entityTypeRegistry';
import { canResearch } from '../research/ResearchButton';
import { useCreateResearch } from '../research/useCreateResearch';

const DealIcon = getEntityTypeIconComponent('ahoy_deal');

export interface StageGroup {
  name: string;
  stages: { entity_id: string; label: string }[];
}

export interface UseRecordActionsParams {
  object: CrmObject;
  entityTypeName: EntityTypeName;
  viewContext: 'slideout' | 'fullPage';
  /** Required for fullPage so post-delete navigation can resolve a list route. */
  listRoute?: string;
  /** When true, lifecycle stage items are omitted from the menu entirely. */
  hideLifecycleStage?: boolean;
}

export interface UseRecordActionsResult {
  /**
   * Flat actions — Research, Enrich phone number, Delete (destructive). Ready
   * to feed a ResponsiveActionBar (as 'dropdown' placement) or render directly
   * as dropdown items.
   */
  simpleActions: ResponsiveAction[];
  /**
   * Available contact lifecycle-stage groups for the "Lifecycle stage" submenu.
   * Empty when hidden (`hideLifecycleStage`) or the entity has no stages.
   * Set/change of the stage is owned by the consumer (RecordActionMenu) via
   * `useDealStageMove`, so this hook only supplies the option data.
   */
  stageGroups: StageGroup[];
  /** The delete confirmation modal — render once in the consumer's tree. */
  deleteModal: ReactElement;
  /** Merge picker/wizard dialogs — render once in the consumer's tree. */
  mergeElements: ReactElement;
  /** True when there is at least one action (simple or stage) to show. */
  hasActions: boolean;
}

export function useRecordActions({
  object,
  entityTypeName,
  viewContext,
  listRoute,
  hideLifecycleStage,
}: UseRecordActionsParams): UseRecordActionsResult {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { popSlideout } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const { hasFeature } = useFeatureAccess();
  // Chat-backed actions ("Chat with Ahoy about this", "Edit with Ahoy") land
  // on chat routes the seat may not include — a restricted seat gets the
  // paywall there, so never offer the entry point in the first place.
  const hasChatFeature = hasFeature('chat');
  const user = useUser();
  // Mutation-only hook: avoids a second redundant fetch of the same entity
  // the slideout already loaded.
  const { update: updateProperty, delete: deleteObject } =
    useCrmObjectMutations(object.org_id, entityTypeName, object, user?.id);

  // Research for contacts and companies
  const isResearchEnabled = useFeatureFlag('research_feature');
  const isResearchable =
    (entityTypeName === 'ahoy_contact' || entityTypeName === 'ahoy_company') &&
    isResearchEnabled;
  const recordType =
    entityTypeName === 'ahoy_company'
      ? ('company' as const)
      : ('contact' as const);
  const { createResearch, isCreating: isCreatingResearch } = useCreateResearch({
    orgId: object.org_id,
    entityId: object.entity_id,
    recordType,
  });

  // Phone enrichment for contacts
  const isContact = entityTypeName === 'ahoy_contact';
  const {
    info: enrichmentInfo,
    triggerEnrichment,
    isTriggering,
  } = usePhoneEnrichment(object.org_id, isContact ? object : undefined);

  // Lifecycle-stage options for contacts. The consumer renders these as the
  // "Lifecycle stage" submenu and owns set/change via useDealStageMove.
  const { getStagesByType } = usePipelineStages(object.org_id, 'ahoy_contact');

  // Moving a contact's lifecycle stage adds/removes an association on the
  // contact — a write, so a seat without that grant gets no stage submenu.
  const canMoveLifecycleStage = hasPermission(
    'ahoy_contact',
    'ahoy_association_added'
  );

  const stageGroups = useMemo<StageGroup[]>(() => {
    // Callers that don't offer a stage affordance at all (the slideout) opt out.
    if (hideLifecycleStage || !canMoveLifecycleStage) {
      return [];
    }
    const contactStages = getStagesByType('ahoy_contact_lifecycle_stage');
    if (contactStages.length === 0) {
      return [];
    }

    const grouped = new Map<string, { entity_id: string; label: string }[]>();
    for (const stage of contactStages) {
      const pipelineName = stage.pipeline_name || 'default';
      if (!grouped.has(pipelineName)) {
        grouped.set(pipelineName, []);
      }
      grouped.get(pipelineName)?.push({
        entity_id: stage.entity_id,
        label: stage.label,
      });
    }

    return Array.from(grouped.entries()).map(([name, stages]) => ({
      name,
      stages,
    }));
  }, [getStagesByType, hideLifecycleStage, canMoveLifecycleStage]);

  const config = getEntityTypeConfig(entityTypeName);

  // Guard delete action on valid user id - useCRMObject uses userId as the event sourceId
  const canDelete =
    user?.id !== undefined &&
    hasPermission(entityTypeName, 'ahoy_object_deleted');

  const handleDelete = useCallback(async () => {
    // Close the confirm modal immediately (optimistic): the list row is already
    // hidden by the optimistic store delete in deleteObject's onMutate, so
    // there's no reason to hold the modal's "Deleting…" spinner up through the
    // round-trip. We deliberately keep the record view mounted until the
    // mutation settles — navigating/popping before it resolves would unmount the
    // mutation observer and could drop its onError rollback, stranding a failed
    // delete as a hidden-but-not-deleted row. On error the optimistic mutation
    // restores the record and we surface a toast.
    setIsDeleteModalOpen(false);
    setIsDeleting(true);
    // Capture the org/session generation at dispatch so a late 202 that resolves
    // after an org switch/logout is rejected by the job store.
    const dispatchGeneration = currentSingleObjectDeleteJobGeneration();
    try {
      const res = await deleteObject();
      const recordName =
        config?.getDisplayName(object).trim() ||
        config?.displayName ||
        'Record';
      // If an org switch/logout happened while the delete was in flight, the
      // captured generation no longer matches: the current session has moved on.
      // Compute this once so the success toast, the async job dispatch, and the
      // post-delete navigation are all governed by the same guard.
      const isStale =
        currentSingleObjectDeleteJobGeneration() !== dispatchGeneration;
      if (isAsyncSingleObjectDeleteResponse(res)) {
        // startSingleObjectDeleteJob rejects a stale dispatchGeneration itself.
        startSingleObjectDeleteJob(
          {
            orgId: object.org_id,
            jobId: res.job_id,
            totalCount: res.total_count,
            entityTypeName: object.entity_type_name,
            entityId: object.entity_id,
            recordName,
          },
          dispatchGeneration
        );
      } else if (!isStale) {
        // Suppress the sync-delete success toast for a stale session: the new
        // session must not see confirmation for the old session's action.
        toast.success(`${config?.displayName || 'Record'} deleted`);
      }
      // Skip navigation for a stale session — popping the new session's slideout
      // stack, or routing it back to the old org's list, would hijack it.
      if (isStale) {
        return;
      }
      if (viewContext === 'slideout') {
        popSlideout();
      } else if (viewContext === 'fullPage') {
        if (!listRoute) {
          logger.error('Missing listRoute for fullPage delete', undefined, {
            component: 'RecordActionMenu',
            entity_id: object.entity_id,
          });
          toast.error('Navigation failed');
        } else {
          const resolvedRoute = listRoute.replace(
            ':orgId',
            String(object.org_id)
          );
          navigate(resolvedRoute);
        }
      }
    } catch (error) {
      logger.error(
        'Failed to delete record',
        error instanceof Error ? error : new Error(String(error)),
        {
          component: 'crm',
          entity_id: object.entity_id,
          entity_type: entityTypeName,
          org_id: object.org_id,
        }
      );
      toast.error("We couldn't delete the record. Try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteObject,
    config,
    object,
    viewContext,
    listRoute,
    entityTypeName,
    popSlideout,
    navigate,
  ]);

  const handleEnrich = useCallback(async () => {
    await triggerEnrichment(object.entity_id);
  }, [triggerEnrichment, object.entity_id]);

  // Create deal — available from contact and company records. Opens the deal
  // create modal pre-filled with this record (and, for a contact, its company).
  const { createDealForContact, createDealForCompany } = useCreateDeal(
    object.org_id
  );
  const showCreateDealAction =
    user?.id !== undefined &&
    (entityTypeName === 'ahoy_contact' || entityTypeName === 'ahoy_company') &&
    hasPermission('ahoy_deal', 'ahoy_object_created');
  const handleCreateDeal = useCallback(() => {
    if (entityTypeName === 'ahoy_company') {
      createDealForCompany(object);
    } else {
      createDealForContact(object);
    }
  }, [entityTypeName, object, createDealForCompany, createDealForContact]);

  // Archive/activate — booking types only. Toggles ahoy_is_active between
  // "true" and "false" via the CRM event pipeline (same as inline edit).
  const isBookingType = entityTypeName === 'ahoy_booking_type';
  const canToggleActive = hasPermission(
    entityTypeName,
    'ahoy_property_updated'
  );
  const isActive =
    getPropertyValue<string>(object.properties?.ahoy_is_active) !== 'false';
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  const handleToggleActive = useCallback(async () => {
    if (isTogglingActive) {
      return;
    }
    setIsTogglingActive(true);
    try {
      await updateProperty('ahoy_is_active', isActive ? 'false' : 'true');
      toast.success(
        isActive ? 'Booking link archived' : 'Booking link activated'
      );
    } catch (error) {
      logger.error(
        'Failed to toggle booking link active state',
        error instanceof Error ? error : new Error(String(error)),
        {
          component: 'crm',
          entity_id: object.entity_id,
          org_id: object.org_id,
        }
      );
      toast.error(
        isActive
          ? "We couldn't archive the booking link. Try again."
          : "We couldn't activate the booking link. Try again."
      );
    } finally {
      setIsTogglingActive(false);
    }
  }, [
    isTogglingActive,
    updateProperty,
    isActive,
    object.entity_id,
    object.org_id,
  ]);

  // Convert to task — notes only. Gated on note-kind 'note' (a note already in
  // a stage or with a due date is already surfaced as a task/reminder, so
  // conversion is meaningless for it) and write permission. The backend
  // endpoint enforces permission independently — this gate is a UX
  // affordance, not a security boundary.
  const isNote = entityTypeName === 'ahoy_note';
  const noteKind = isNote ? getNoteKindFromObject(object) : null;
  const showConvertToTaskAction =
    user?.id !== undefined &&
    isNote &&
    noteKind === 'note' &&
    hasPermission(entityTypeName, 'ahoy_property_updated');
  const { convertNoteToTask, isConverting } = useConvertNoteToTask(
    object.org_id
  );
  const isConvertingToTask = isConverting(object.entity_id);
  const handleConvertToTask = useCallback(async () => {
    try {
      await convertNoteToTask(object.entity_id);
      toast.success('Note converted to task');
    } catch {
      // Error already logged and surfaced via toast inside convertNoteToTask.
    }
  }, [convertNoteToTask, object.entity_id]);

  const handleResearch = useCallback(async () => {
    await createResearch();
  }, [createResearch]);

  const handleChatAbout = useCallback(() => {
    const displayName =
      config?.getDisplayName(object).trim() || config?.displayName || 'Record';
    const { pathname, state } = buildChatAboutNavState({
      orgId: object.org_id,
      entityId: object.entity_id,
      entityType: entityTypeName,
      displayName,
    });
    navigate(pathname, { state });
  }, [config, object, entityTypeName, navigate]);

  // "Edit with Ahoy" — opens builder mode with this record in the panel, so
  // the assistant rewrites it while it stays in view. Distinct from "Chat with
  // Ahoy about this" above, which starts an ordinary conversation ABOUT the
  // record and leaves it behind.
  const handleEditInBuilder = useCallback(() => {
    // NAMES THE RECORD. This used to say only "this <type>", on the reasoning
    // that the record was open in the panel beside the chat and so did not
    // need referencing. That is no longer true on mobile, where the chat is
    // full screen and the panel opens only on demand — "this automation" then
    // has no visible referent at all. Naming it also makes the conversation
    // legible later, when it is one row in a list of builds.
    const typeLabel = (config?.displayName || 'record').toLowerCase();
    const recordName = getDisplayName(object, typeLabel);
    const { to, state } = buildEditInBuilderNavState(
      object.org_id,
      entityTypeName,
      object.entity_id,
      `I want to change the ${typeLabel} "${recordName}"`
    );
    navigate(to, { state });
  }, [config, object, entityTypeName, navigate]);

  const showBuilderAction = useCanEditInBuilder(entityTypeName);

  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);

  // Guard enrich action on valid user id - triggerEnrichment requires user.id.
  // Enrichment and research both CREATE a CRM record (an enrichment request /
  // a research entity), so a seat without that create grant never sees them.
  const showEnrichAction =
    user?.id !== undefined &&
    isContact &&
    hasPermission('ahoy_enrichment_request', 'ahoy_object_created') &&
    (enrichmentInfo?.isEligible || isTriggering);

  const showResearchAction =
    user?.id !== undefined &&
    isResearchable &&
    hasPermission('ahoy_research', 'ahoy_object_created') &&
    canResearch(object, recordType);

  const { mergeAction, mergeElements } = useMergeRecordAction({
    object,
    entityTypeName,
    viewContext,
    listRoute,
  });

  const simpleActions = useMemo<ResponsiveAction[]>(() => {
    const actions: ResponsiveAction[] = [];
    if (showResearchAction) {
      actions.push({
        key: 'research',
        label: isCreatingResearch ? 'Researching…' : 'Research',
        icon: SearchLg,
        onClick: handleResearch,
        isDisabled: isCreatingResearch,
        placement: 'dropdown',
      });
    }
    if (showEnrichAction) {
      actions.push({
        key: 'enrich',
        label: isTriggering ? 'Enriching…' : 'Enrich phone number',
        icon: Stars02,
        onClick: handleEnrich,
        isDisabled: isTriggering,
        placement: 'dropdown',
      });
    }
    // Chat about this record — available when authenticated on a seat that
    // includes Chat (the route is feature-gated; see FeatureGate).
    if (user && hasChatFeature) {
      actions.push({
        key: 'chat',
        label: 'Chat with Ahoy about this',
        icon: MessageChatCircle,
        onClick: handleChatAbout,
        placement: 'dropdown',
      });
    }
    if (showBuilderAction && user) {
      actions.push({
        key: 'edit-in-builder',
        label: 'Edit with Ahoy',
        icon: Edit03,
        onClick: handleEditInBuilder,
        placement: 'dropdown',
      });
    }
    if (showCreateDealAction) {
      actions.push({
        key: 'create-deal',
        label: 'Create deal',
        icon: DealIcon,
        onClick: handleCreateDeal,
        placement: 'dropdown',
      });
    }
    if (showConvertToTaskAction) {
      actions.push({
        key: 'convert-to-task',
        label: isConvertingToTask ? 'Converting…' : 'Convert to task',
        icon: CheckDone01,
        onClick: handleConvertToTask,
        isDisabled: isConvertingToTask,
        placement: 'dropdown',
      });
    }
    if (isBookingType && user && canToggleActive) {
      actions.push({
        key: 'toggle-active',
        label: isActive ? 'Archive' : 'Activate',
        icon: isActive ? Archive : RefreshCcw01,
        onClick: handleToggleActive,
        isDisabled: isTogglingActive,
        placement: 'dropdown',
      });
    }
    if (mergeAction) {
      actions.push(mergeAction);
    }
    if (canDelete) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        icon: Trash01,
        onClick: openDeleteModal,
        isDisabled: isDeleting,
        destructive: true,
        placement: 'dropdown',
      });
    }
    return actions;
  }, [
    showResearchAction,
    isCreatingResearch,
    handleResearch,
    showEnrichAction,
    isTriggering,
    handleEnrich,
    user,
    hasChatFeature,
    handleChatAbout,
    showBuilderAction,
    handleEditInBuilder,
    showCreateDealAction,
    handleCreateDeal,
    showConvertToTaskAction,
    isConvertingToTask,
    handleConvertToTask,
    isBookingType,
    canToggleActive,
    isActive,
    handleToggleActive,
    isTogglingActive,
    mergeAction,
    canDelete,
    isDeleting,
    openDeleteModal,
  ]);

  // The menu shows only when it has something to offer. For a full seat
  // "Chat with Ahoy about this" guarantees at least one entry; a restricted
  // seat with no chat and no write grants gets no empty kebab.
  const hasActions = simpleActions.length > 0 || stageGroups.length > 0;

  const deleteModal = (
    <DeleteCRMObjectModal
      isOpen={isDeleteModalOpen}
      onOpenChange={setIsDeleteModalOpen}
      entityTypeLabel={config?.displayName || 'record'}
      objectName={config ? config.getDisplayName(object) : 'this record'}
      onConfirm={handleDelete}
      isDeleting={isDeleting}
    />
  );

  return {
    simpleActions,
    stageGroups,
    deleteModal,
    mergeElements,
    hasActions,
  };
}
